/**
 * Session ad-value accumulator (yoto `tracking.ts`).
 *
 * Every priced ad impression is fed in via `trackBid`, which keeps a running CPM
 * sum for the page and for the browser session and emits two signals: one per
 * bid, and one per cent milestone the session value crosses. Traffic-source
 * pixels report those milestones back so the buying algorithms can optimise for
 * user value.
 *
 * Values are CPM sums throughout — session revenue in cents is `sessionCpm / 10`.
 * Milestones step by a quarter cent below `WHOLE_CENTS_FROM` and by whole cents
 * above it, and the last one reached is persisted so a new page view resumes
 * rather than re-reporting.
 *
 * Keys are `kz_`-prefixed so a page running this alongside yoto-web keeps two
 * independent tallies instead of double-counting one.
 */

import { emit, SdkEvent } from '../events/bus';
import { debug } from '../../util/log';

const SESSION_CPM_KEY = 'kz_session_cpm';
const LAST_MILESTONE_KEY = 'kz_last_session_value';
const WHOLE_CENTS_FROM = 2;

let pageCpm = 0;

function readNumber(key: string): number {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return 0;
    const value = parseFloat(raw);
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

function writeNumber(key: string, value: number): void {
  try {
    sessionStorage.setItem(key, String(value));
  } catch {
    /* ignore */
  }
}

/** Floor to a quarter, via two decimals so float noise can't round a cent up. */
function floorToQuarter(value: number): number {
  const twoDecimals = Math.trunc(value * 100) / 100;
  return parseFloat((Math.floor(twoDecimals * 4) / 4).toFixed(2));
}

/**
 * Emits every milestone between the last one reached and the current value, so a
 * single large bid still reports each step it skipped over.
 */
function emitCentMilestones(sessionCpm: number): void {
  const valueInCents = sessionCpm / 10;
  const lastReached = readNumber(LAST_MILESTONE_KEY);

  let reached = Math.floor(valueInCents);
  let step = 1;
  if (lastReached < WHOLE_CENTS_FROM) {
    const quarter = floorToQuarter(valueInCents);
    reached = quarter < WHOLE_CENTS_FROM ? quarter : reached;
    step = 0.25;
  }

  if (reached <= lastReached) return;
  writeNumber(LAST_MILESTONE_KEY, reached);

  for (let milestone = lastReached + step; milestone <= reached; ) {
    emit(SdkEvent.SESSION_BID_CENT_INCREMENT, milestone);
    milestone += milestone < WHOLE_CENTS_FROM ? 0.25 : 1;
  }
}

/** Counts one ad impression's CPM into the page and session totals. */
export function trackBid(bidCpm: number): void {
  if (!Number.isFinite(bidCpm) || bidCpm <= 0) return;

  const sessionCpm = readNumber(SESSION_CPM_KEY) + bidCpm;
  writeNumber(SESSION_CPM_KEY, sessionCpm);
  pageCpm += bidCpm;

  debug('bid counted', { bidCpm, pageCpm, sessionCpm });
  emit(SdkEvent.SESSION_BID_CHANGE, { pageCpm, sessionCpm, bidCpm });
  emitCentMilestones(sessionCpm);
}

/** Test-only */
export function _resetSessionValueForTests(): void {
  pageCpm = 0;
}
